import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, Alert, Row, Col, message, Modal } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './NewHome.css';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.GOOGLE_CLIENT_ID;

const Register = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [turnstileModalVisible, setTurnstileModalVisible] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleBindLoading, setGoogleBindLoading] = useState(false);
  const [googleBindModalVisible, setGoogleBindModalVisible] = useState(false);
  const [googleBindPassword, setGoogleBindPassword] = useState('');
  const [googleBindEmail, setGoogleBindEmail] = useState('');
  const [googleIdToken, setGoogleIdToken] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const originalTitle = document.title;
    document.title = `Register - ${originalTitle}`;

    return () => {
      document.title = originalTitle;
    };
  }, []);

  const completeLoginFlow = useCallback(async (accessToken) => {
    localStorage.setItem('token', accessToken);

    const userResponse = await authAPI.getProfile();
    const userInfo = userResponse.data;

    const loginSuccess = await onLogin(accessToken);
    if (!loginSuccess) {
      setError('Failed to update authentication state');
      return false;
    }

    const pendingActionStr = localStorage.getItem('pending_action');
    if (pendingActionStr) {
      try {
        const pendingAction = JSON.parse(pendingActionStr);
        localStorage.removeItem('pending_action');

        if (userInfo.role === 'dropshipper' && pendingAction.action === 'order_samples') {
          const productData = {
            product_id: pendingAction.product_id,
            product_name: pendingAction.product_name,
            variant_path: pendingAction.variant_path,
            variant_data: pendingAction.variant_data,
            quantity: 1
          };
          localStorage.setItem('selected_product_for_order', JSON.stringify(productData));
          navigate(pendingAction.redirect_url);
          return true;
        }
      } catch (error) {
        localStorage.removeItem('pending_action');
      }
    }

    if (userInfo.role === 'admin') {
      navigate('/admin');
    } else if (userInfo.role === 'dropshipper') {
      navigate('/dropshipper');
    } else {
      navigate('/dashboard');
    }
    return true;
  }, [navigate, onLogin]);

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    if (!response?.credential) {
      return;
    }
    setGoogleLoading(true);
    setError('');

    try {
      const res = await authAPI.googleLogin({ id_token: response.credential });
      const data = res.data;

      if (data.requires_password) {
        setGoogleBindEmail(data.email || '');
        setGoogleIdToken(response.credential);
        setGoogleBindPassword('');
        setGoogleBindModalVisible(true);
        return;
      }

      if (data.access_token) {
        await completeLoginFlow(data.access_token);
      } else {
        setError('Google sign-in failed: missing access token');
      }
    } catch (err) {
      if (err.response) {
        const detail = err.response.data?.detail;
        setError(detail || 'Google sign-in failed');
      } else if (err.request) {
        setError('Network error, please try again later');
      } else {
        setError(`Google sign-in error: ${err.message}`);
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [completeLoginFlow]);

  const initializeGoogleButton = useCallback(() => {
    if (!GOOGLE_CLIENT_ID || !window.google?.accounts?.id) {
      return;
    }
    const container = document.getElementById('google-register-button');
    if (!container) {
      return;
    }
    container.innerHTML = '';

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCredentialResponse,
    });
    window.google.accounts.id.renderButton(container, {
      theme: 'outline',
      size: 'large',
      shape: 'rectangular',
      text: 'continue_with',
      width: 320,
    });
  }, [handleGoogleCredentialResponse]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('GOOGLE_CLIENT_ID not set, Google login is disabled');
      return;
    }

    const handleScriptLoad = () => initializeGoogleButton();
    const existingScript = document.getElementById('google-identity-script');

    if (existingScript) {
      if (window.google?.accounts?.id) {
        initializeGoogleButton();
      } else {
        existingScript.addEventListener('load', handleScriptLoad);
      }

      return () => {
        existingScript.removeEventListener('load', handleScriptLoad);
      };
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = 'google-identity-script';
    script.onload = handleScriptLoad;
    document.head.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, [initializeGoogleButton]);

  // 加载Cloudflare Turnstile脚本
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      
        return;
      }

    // 检查脚本是否已加载
    if (window.turnstile) {
        return;
      }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // 清理脚本
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Turnstile验证成功后的发送验证码逻辑
  const sendVerificationCode = async (email, token) => {
    try {
      setSendingCode(true);
      setError('');

      // 调用发送验证码API，包含turnstile token
      await axios.post(`${API_BASE_URL}/register/send-code`, {
        email,
        turnstile_token: token
      });
      
      message.success('Verification code sent successfully! Please check your email.');
      
      // 开始60秒倒计时
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err) {
      
      
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        
        if (status === 400) {
          if (detail === 'Email already registered') {
            setError('This email is already registered. Please login instead.');
          } else if (detail === 'Invalid email format') {
            setError('Please enter a valid email address.');
          } else if (detail === 'Invalid turnstile token') {
            setError('Verification failed. Please try again.');
          } else {
            setError(detail || 'Invalid request');
          }
        } else if (status === 429) {
          setError('Please wait 60 seconds before requesting another code');
        } else {
          setError(`Server error (${status}): ${detail || 'Unknown error'}`);
        }
      } else if (err.request) {
        setError('Unable to connect to server. Please check your network connection.');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setSendingCode(false);
    }
  };

  // 显示Turnstile验证模态框
  const showTurnstileModal = (email) => {
    setTurnstileModalVisible(true);
    setTurnstileToken(null);

    // 等待脚本加载完成后渲染Turnstile
    setTimeout(() => {
      if (window.turnstile && TURNSTILE_SITE_KEY) {
        // 先清理可能存在的旧widget
        const container = document.getElementById('turnstile-container');
        if (container) {
          container.innerHTML = '';
        }

        window.turnstile.render('#turnstile-container', {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => {
            setTurnstileToken(token);
            // 验证成功后自动发送验证码
            setTurnstileModalVisible(false);
            sendVerificationCode(email, token);
          },
          'error-callback': () => {
            message.error('Verification failed. Please try again.');
          },
          'expired-callback': () => {
            setTurnstileToken(null);
            message.warning('Verification expired. Please try again.');
          }
        });
      } else {
        message.error('Turnstile not loaded. Please refresh the page.');
        setTurnstileModalVisible(false);
      }
    }, 100);
  };

  // 处理模态框关闭
  const handleModalCancel = () => {
    // 清理Turnstile widget
    const container = document.getElementById('turnstile-container');
    if (container) {
      container.innerHTML = '';
    }

    setTurnstileModalVisible(false);
    setTurnstileToken(null);
    // 如果有正在进行的发送验证码操作，也要取消
    setSendingCode(false);
  };

  // 发送验证码（入口函数）
  const handleSendCode = async () => {
    try {
      // 验证邮箱字段
      const email = form.getFieldValue('email');
      if (!email) {
        message.error('Please enter your email address');
        return;
      }

      // 验证邮箱格式
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        message.error('Please enter a valid email address');
        return;
      }

      // 检查Turnstile是否可用
      if (!TURNSTILE_SITE_KEY) {
        message.error('Verification service not available. Please contact support.');
        return;
      }

      // 显示Turnstile验证模态框
      showTurnstileModal(email);

    } catch (err) {
      
      setError(`Error: ${err.message}`);
    }
  };

  const handleGoogleBind = async () => {
    if (!googleIdToken) {
      setError('Missing Google credential, please try again');
      setGoogleBindModalVisible(false);
      return;
    }
    if (!googleBindPassword) {
      message.error('Please enter your password to link this account');
      return;
    }

    setGoogleBindLoading(true);
    setError('');
    try {
      const res = await authAPI.googleLogin({
        id_token: googleIdToken,
        password: googleBindPassword,
      });
      const data = res.data;

      if (data.access_token) {
        setGoogleBindModalVisible(false);
        setGoogleBindPassword('');
        setGoogleIdToken('');
        await completeLoginFlow(data.access_token);
      } else {
        setError('Linking failed, please try again');
      }
    } catch (err) {
      if (err.response) {
        const detail = err.response.data?.detail;
        setError(detail || 'Linking failed, please check your password');
      } else if (err.request) {
        setError('Network error, please try again later');
      } else {
        setError(`Linking failed: ${err.message}`);
      }
    } finally {
      setGoogleBindLoading(false);
    }
  };

  // 注册提交
  const onFinish = async (values) => {
    setLoading(true);
    setError('');
    
    try {
      // 调用注册API
      const response = await axios.post(`${API_BASE_URL}/register`, {
        username: values.username,
        email: values.email,
        password: values.password,
        confirm_password: values.confirm_password,
        verification_code: values.verification_code,
      });

      const { access_token } = response.data;
      
      const success = await completeLoginFlow(access_token);
      if (success) {
        message.success('Registration successful! Welcome to CHdropshipping!');
      } else {
        setError('Failed to update authentication state');
      }
      
    } catch (err) {
      
      
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        
        if (status === 400) {
          if (detail === 'Username already exists') {
            setError('This username is already taken. Please choose another one.');
          } else if (detail === 'Email already registered') {
            setError('This email is already registered. Please login instead.');
          } else if (detail === 'Invalid or expired verification code') {
            setError('Invalid or expired verification code. Please request a new one.');
          } else if (detail === 'Passwords do not match') {
            setError('Passwords do not match. Please check and try again.');
          } else if (detail === 'Username must contain only letters and numbers') {
            setError('Username must contain only letters and numbers.');
          } else if (detail === 'Password must be at least 6 characters long') {
            setError('Password must be at least 6 characters long.');
          } else {
            setError(detail || 'Registration failed');
          }
        } else {
          setError(`Server error (${status}): ${detail || 'Unknown error'}`);
        }
      } else if (err.request) {
        setError('Unable to connect to server. Please check your network connection.');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'radial-gradient(circle at 10% 10%, rgba(210, 226, 255, 0.6), transparent 50%), radial-gradient(circle at 90% 0%, rgba(179, 205, 255, 0.35), transparent 55%), linear-gradient(180deg, #fafdff 0%, #eef2ff 100%)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {error && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 2000
          }}
        >
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ maxWidth: 480, width: '90%' }}
          />
        </div>
      )}
      <div style={{ maxWidth: '1100px', margin: '0 auto', width: '100%', padding: '0 32px' }}>
        <Header />
      </div>

      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 0'
      }}>
        <Row justify="center" style={{ width: '100%' }}>
        <Col xs={20} sm={16} md={12} lg={8} xl={6}>
          <Card 
            title={
              <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }}>
                Register
              </div>
            }
            style={{ 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: '10px'
            }}
          >
            <Form
              form={form}
              name="register"
              onFinish={onFinish}
              size="large"
              layout="vertical"
            >
              {GOOGLE_CLIENT_ID && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    id="google-register-button"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      pointerEvents: googleLoading ? 'none' : 'auto',
                      opacity: googleLoading ? 0.6 : 1
                    }}
                  ></div>
                </div>
              )}
              
              <Form.Item
                label="Username"
                name="username"
                rules={[
                  { required: true, message: 'Please enter your username!' },
                  { 
                    pattern: /^[A-Za-z0-9]+$/, 
                    message: 'Username must contain only letters and numbers!' 
                  },
                ]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Enter username" 
                />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Please enter your email!' },
                  { type: 'email', message: 'Please enter a valid email!' },
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="Enter email address"
                  suffix={
                    <Button 
                      type="link" 
                      onClick={handleSendCode}
                      loading={sendingCode}
                      disabled={countdown > 0}
                      style={{ padding: 0 }}
                    >
                      {countdown > 0 ? `${countdown}s` : 'Send Code'}
                    </Button>
                  }
                />
              </Form.Item>

              <Form.Item
                label="Verification Code"
                name="verification_code"
                rules={[
                  { required: true, message: 'Please enter verification code!' },
                  { len: 6, message: 'Verification code must be 6 digits!' },
                ]}
              >
                <Input 
                  prefix={<SafetyOutlined />} 
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: 'Please enter your password!' },
                  { min: 6, message: 'Password must be at least 6 characters!' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter password (min 6 characters)"
                />
              </Form.Item>

              <Form.Item
                label="Confirm Password"
                name="confirm_password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirm password"
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  style={{ width: '100%', height: '40px' }}
                >
                  Register
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center' }}>
                Already have an account?{' '}
                <Button type="link" onClick={() => navigate('/login')} style={{ padding: 0 }}>
                  Login here
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Link existing account"
        open={googleBindModalVisible}
        onCancel={() => {
          setGoogleBindModalVisible(false);
          setGoogleBindPassword('');
          setGoogleIdToken('');
        }}
        onOk={handleGoogleBind}
        okText="Link and sign in"
        cancelText="Cancel"
        confirmLoading={googleBindLoading}
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          We found the email {googleBindEmail || 'this email'} in our system. Enter your password to link it.
        </p>
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="Enter password to link"
          value={googleBindPassword}
          onChange={(e) => setGoogleBindPassword(e.target.value)}
          onPressEnter={handleGoogleBind}
        />
      </Modal>

      {/* Turnstile验证模态框 */}
      <Modal
        title="Security Check"
        open={turnstileModalVisible}
        footer={null}
        closable={true}
        centered
        width={400}
        onCancel={handleModalCancel}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div id="turnstile-container"></div>
          <div style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
            Please tick the box to complete the security check
          </div>
        </div>
      </Modal>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', width: '100%', padding: '0 32px' }}>
        <Footer />
      </div>
    </div>
  );
};

export default Register;
